import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from '../common/Card';
import Button from '../common/Button';
import FormSidebar from './FormSidebar';
import FormSection from './FormSection';
import TextInputSection from './TextInputSection';
import ImageUploadSection from './ImageUploadSection';
import DicomFileUpload from './DicomFileUpload';
import Spinner from '../common/Spinner';
import { reportService } from '../../services/reportService';
import { patientService } from '../../services/patientService';
import Input from '../common/Input';
import Select from '../common/Select';
import { format } from 'date-fns';
import { uploadImageToS3 } from '../../utils/uploadImageToS3';
import { requestPDF } from '../../services/pdfService';
import PublishingOverlay from '../common/PublishingOverlay';
import { generateReportHTML } from '../../services/reportPreviewService';
import ValidationModal from './ValidationModal';
import { validateDEXAReport } from '../../utils/dexaValidation';
import { generatePDFFilename } from '../../utils/generatePDFFilename';

// BMD Table section component
const regions = {
  apSpine: [
    { region: "L1" },
    { region: "L2" },
    { region: "L3" },
    { region: "L4" },
    { region: "L1-L4" },
  ],
  rightFemur: [
    { region: "NECK RIGHT" },
    { region: "TOTAL RIGHT" },
  ],
  leftFemur: [
    { region: "NECK LEFT" },
    { region: "TOTAL LEFT" },
  ],
};

const ImageUpload = ({ label, value, onChange, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');

  const handleImageChange = async (e) => {
    if (disabled || uploading) return;
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    setUploading(true);
    try {
      setPreview(URL.createObjectURL(file));
      const s3Url = await uploadImageToS3(file);
      setPreview(s3Url);
      onChange(s3Url);
    } catch (err) {
      alert('Failed to upload image.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    if (disabled || uploading) return;
    setPreview('');
    onChange('');
  };

  return (
    <div className="flex flex-col items-center mb-2">
      {preview ? (
        <img
          src={preview}
          alt={label}
          className="w-28 h-40 object-contain border rounded mb-1"
        />
      ) : (
        <div className="w-28 h-40 flex items-center justify-center border rounded bg-gray-100 text-gray-400 mb-1 text-xs">
          No Image
        </div>
      )}
      <input
        type="file"
        accept="image/*"
        disabled={disabled || uploading}
        onChange={handleImageChange}
        className="block text-xs"
        style={{ width: "110px" }}
      />
      {uploading && <span className="text-xs text-primary-600 mt-1">Uploading...</span>}
      <span className="text-xs text-gray-500">{label}</span>
      {preview && !disabled && (
        <Button
          variant="secondary"
          className="mt-1"
          onClick={handleRemoveImage}
          disabled={uploading}
        >
          Remove Image
        </Button>
      )}
    </div>
  );
};

const TableSection = ({
  title,
  sectionKey,
  data,
  onChange,
  imageUrl,
  onImageChange,
  disabled,
}) => (
  <div className="mb-6 flex gap-4 items-start">
    <ImageUpload
      label={title}
      value={imageUrl}
      onChange={img => onImageChange(sectionKey, img)}
      disabled={disabled}
    />
    <div className="flex-1 border rounded p-2">
      <div className="font-bold mb-2">{title}</div>
      <table className="min-w-full text-sm">
        <thead>
          <tr>
            <th className="px-2 py-1 text-left">REGION</th>
            <th className="px-2 py-1 text-left">BMD (g/cm²)</th>
            <th className="px-2 py-1 text-left">T SCORE</th>
            <th className="px-2 py-1 text-left">Z SCORE</th>
          </tr>
        </thead>
        <tbody>
          {regions[sectionKey].map((row, idx) => (
            <tr key={row.region}>
              <td className="px-2 py-1">{row.region}</td>
              <td className="px-2 py-1">
                <input
                  type="number"
                  step="0.001"
                  value={(data && data[idx] && data[idx].bmd) || ""}
                  disabled={disabled}
                  onChange={e =>
                    onChange(sectionKey, idx, "bmd", e.target.value)
                  }
                  className="input"
                  style={{ width: 140 }}
                />
              </td>
              <td className="px-2 py-1">
                <input
                  type="number"
                  step="0.1"
                  value={(data && data[idx] && data[idx].tScore) || ""}
                  disabled={disabled}
                  onChange={e =>
                    onChange(sectionKey, idx, "tScore", e.target.value)
                  }
                  className="input"
                  style={{ width: 120 }}
                />
              </td>
              <td className="px-2 py-1">
                <input
                  type="number"
                  step="0.1"
                  value={(data && data[idx] && data[idx].zScore) || ""}
                  disabled={disabled}
                  onChange={e =>
                    onChange(sectionKey, idx, "zScore", e.target.value)
                  }
                  className="input"
                  style={{ width: 120 }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const BMDTableSection = ({
  bmdSections,
  bmdImages,
  onBmdChange,
  onBmdImageChange,
  isEditingAllowed,
}) => (
  <div>
    <TableSection 
      title="AP: SPINE"
      sectionKey="apSpine"
      data={bmdSections.apSpine}
      onChange={onBmdChange}
      imageUrl={bmdImages.apSpine}
      onImageChange={onBmdImageChange}
      disabled={!isEditingAllowed}
    />
    <TableSection
      title="LEFT FEMUR"
      sectionKey="leftFemur"
      data={bmdSections.leftFemur}
      onChange={onBmdChange}
      imageUrl={bmdImages.leftFemur}
      onImageChange={onBmdImageChange}
      disabled={!isEditingAllowed}
    />


    <TableSection
      title="RIGHT FEMUR"
      sectionKey="rightFemur"
      data={bmdSections.rightFemur}
      onChange={onBmdChange}
      imageUrl={bmdImages.rightFemur}
      onImageChange={onBmdImageChange}
      disabled={!isEditingAllowed}
    />
  </div>
);

const BodyAsymmetrySection = ({
  ffmi,
  almi,
  imageUrl,
  limbComposition,
  onChange,
  onImageChange,
  disabled
}) => (
  <div>
    {/* <div className="flex gap-4 mb-6">
      <div className="flex-1 border rounded p-4">
        <div className="text-sm text-gray-600 mb-2">FFMI (FAT FREE MASS INDEX)</div>
        <input
          type="number"
          value={ffmi || ""}
          onChange={(e) => onChange("ffmi", null, e.target.value)}
          className="input w-full"
          placeholder="Enter FFMI value"
          disabled={disabled}
        />
      </div>
      <div className="flex-1 border rounded p-4">
        <div className="text-sm text-gray-600 mb-2">ALMI (APPENDICULAR LEAN MASS INDEX)</div>
        <input
          type="number"
          value={almi || ""}
          onChange={(e) => onChange("almi", null, e.target.value)}
          className="input w-full"
          placeholder="Enter ALMI value"
          disabled={disabled}
        />
      </div>
    </div> */}

    <div className="mb-6">
      <ImageUploadSection
        label="Body Asymmetry Image"
        value={imageUrl}
        onChange={onImageChange}
        disabled={disabled}
      />
    </div>

    <div className="border rounded p-4 mb-6">
      <div className="font-bold mb-4">Limb Composition</div>
      <table className="min-w-full">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2">Region</th>
            <th className="text-left py-2">Fat (g)</th>
            <th className="text-left py-2">Lean (g)</th>
            <th className="text-left py-2">BMC (g)</th>
          </tr>
        </thead>
        <tbody>
          {['rightArm', 'leftArm', 'rightLeg', 'leftLeg', 'trunk'].map((limb) => (
            <tr key={limb}>
              <td className="py-2">{limb.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</td>
              <td className="py-2">
                <input
                  type="number"
                  value={limbComposition[limb]?.fat || ""}
                  onChange={(e) => onChange(limb, "fat", e.target.value)}
                  className="input w-24"
                  disabled={disabled}
                />
              </td>
              <td className="py-2">
                <input
                  type="number"
                  value={limbComposition[limb]?.lean || ""}
                  onChange={(e) => onChange(limb, "lean", e.target.value)}
                  className="input w-24"
                  disabled={disabled}
                />
              </td>
              <td className="py-2">
                <input
                  type="number"
                  value={limbComposition[limb]?.bmc || ""}
                  onChange={(e) => onChange(limb, "bmc", e.target.value)}
                  className="input w-24"
                  disabled={disabled}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const formatNumber = (id) => {
  return String(id).padStart(6, '0');
};

const ReportForm = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [reportId, setReportId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [patient, setPatient] = useState(null);
  const [existingReport, setExistingReport] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [isEditingAllowed, setIsEditingAllowed] = useState(true);
  const [currentUser] = useState('ravikumar28');
  const [currentDateTime] = useState('2025-05-31 10:43:02');
  const [isPublishing, setIsPublishing] = useState(false);
  const [validationModal, setValidationModal] = useState({ isOpen: false, violations: [], action: null });
  const [bypassValidation, setBypassValidation] = useState(false);

  const [formData, setFormData] = useState({
    visit_date: format(new Date(), 'yyyy-MM-dd'),
    name: null,
    height: null,
    weight: null,
    age: null,
    gender: null,
    dob: null,
    date_of_exam: format(new Date(), 'yyyy-MM-dd'),
    fat_mass: null,
    lean_mass: null,
    bmc: null,
    total_mass: null,
    images: [
      { url: null, name: 'Asymmetry', type: 'asymmetry' },
    ],
    visceral_fat_area: null,
    visceral_fat: null,
    andriod_gynoid_fat: null,
    gynoid_fat: null,
    body_composition_trunk: null,
    right_arm_status: null,
    left_arm_status: null,
    right_leg_status: null,
    left_leg_status: null,
    asymmetry_status: null,
    bmdSections: {
      apSpine: [
        { region: "L1", bmd: null, tScore: null, zScore: null },
        { region: "L2", bmd: null, tScore: null, zScore: null },
        { region: "L3", bmd: null, tScore: null, zScore: null },
        { region: "L4", bmd: null, tScore: null, zScore: null },
        { region: "L1-L4", bmd: null, tScore: null, zScore: null }
      ],
      rightFemur: [
        { region: "NECK RIGHT", bmd: null, tScore: null, zScore: null },
        { region: "TOTAL RIGHT", bmd: null, tScore: null, zScore: null }
      ],
      leftFemur: [
        { region: "NECK LEFT", bmd: null, tScore: null, zScore: null },
        { region: "TOTAL LEFT", bmd: null, tScore: null, zScore: null }
      ]
    },
    bmdImages: {
      apSpine: null,
      rightFemur: null,
      leftFemur: null
    },
    report_status: 'submitted',
    asymmetry: {
      imageUrl: null,
      limbComposition: {
        rightArm: { fat: null, lean: null, bmc: null },
        leftArm: { fat: null, lean: null, bmc: null },
        rightLeg: { fat: null, lean: null, bmc: null },
        leftLeg: { fat: null, lean: null, bmc: null },
        trunk: { fat: null, lean: null, bmc: null }
      }
    },
  });

  const sections = [
    {
      id: 'patientInfo',
      label: 'Patient Information',
      fields: [
        { name: 'name', label: 'Name', type: 'input' },
        { name: 'height', label: 'Height (cm)', type: 'input', inputType: 'number' },
        { name: 'weight', label: 'Weight (kg)', type: 'input', inputType: 'number' },
        { name: 'age', label: 'Age (years)', type: 'input', inputType: 'number' },
        {
          name: 'gender', label: 'Gender', type: 'select', options: [
            { value: 'Male', label: 'Male' },
            { value: 'Female', label: 'Female' },
            { value: 'Other', label: 'Other' }
          ]
        },
        { name: 'dob', label: 'Date of Birth', type: 'input', inputType: 'date' },
        { name: 'date_of_exam', label: 'Date of Examination', type: 'input', inputType: 'date' }
      ]
    },
    {
      id: 'bodyComposition',
      label: 'Body Composition',
      fields: [
        { name: 'fat_mass', label: 'Fat Mass (g)', type: 'input', inputType: 'number' },
        { name: 'lean_mass', label: 'Lean Mass (g)', type: 'input', inputType: 'number' },
        { name: 'bmc', label: 'BMC', type: 'input', inputType: 'number' },
        { name: 'total_mass', label: 'Total Mass (g)', type: 'input', inputType: 'number' },
      ]
    },
    {
      id: 'bmdTable',
      label: 'BMD Table',
      fields: [],
      component: 'bmdTable'
    },
    {
      id: 'fatAnalysis',
      label: 'Fat Analysis',
      fields: [
        { name: 'visceral_fat_area', label: 'Visceral Fat Area', type: 'input' },
        { name: 'visceral_fat', label: 'Visceral Fat', type: 'input' },
        { name: 'andriod_gynoid_fat', label: 'Android Fat Ratio', type: 'input' },
        { name: 'gynoid_fat', label: 'Gynoid Fat', type: 'input' }
      ]
    },
    {
      id: 'asymmetry',
      label: 'Body Asymmetry',
      fields: [],
      component: 'bodyAsymmetry'
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch patient data
        const patientResponse = await patientService.getPatientById(patientId);
        console.log("patientResponse", patientResponse.data.data.id);
        setReportId(patientResponse.data.data.id);
        console.log("reportId", reportId);
        if (patientResponse.data && patientResponse.data.success) {
          const patientData = patientResponse.data.data;
          setPatient(patientData);
          // Always allow editing
          setIsEditingAllowed(true);

          // Pre-populate patient data from appointment
          if (patientData.profile) {
            setFormData(prev => ({
              ...prev,
              name: patientData.profile.name,
              gender: patientData.profile.gender === 'male' ? 'Male' : 'Female',
              dob: patientData.profile.dob ? new Date(patientData.profile.dob).toISOString().split('T')[0] : '',
              age: patientData.profile.age || '',
              reportId: formatNumber(patientData.id),
              patientId: formatNumber(patientData.profile.id)
            }));

            // Generate and log filename on page load
            const fileName = generatePDFFilename(
              patientData.profile.name,
              'dexa',
              patientData.sukra_order_id || patientData.id
            );
            console.log('📄 Generated PDF Filename:', fileName);
            console.log('Patient Name:', patientData.profile.name);
            console.log('Order ID:', patientData.sukra_order_id || patientData.id);
          }
        }

        // Check if report already exists
        try {
          const reportResponse = await reportService.getReportByPatientId(patientId);
          if (reportResponse.data && reportResponse.data.success && reportResponse.data.data) {
            const reportData = reportResponse.data.data;
            setExistingReport(reportData);

            // Pre-fill form with existing data if available
            setFormData(prev => ({
              ...prev,
              ...reportData,
              suggestion_details: {
                training: reportData.suggestion_details?.training || '',
                nutrition: reportData.suggestion_details?.nutrition || ''
              },
              images: reportData.images && reportData.images.length > 0 ? reportData.images : prev.images,
              bmdSections: reportData.bmdSections || prev.bmdSections,
              bmdImages: reportData.bmdImages || prev.bmdImages,
            }));
          }
        } catch (error) {
          // No report exists, continue with empty form (we're creating a new report)
          if (error.response && error.response.status !== 404) {
            throw error;
          }
        }
      } catch (error) {
        toast.error('Failed to load patient data');
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line
  }, [patientId]);

  const getNestedValue = (obj, path) => {
    if (!obj || !path) return '';

    return path.split('.').reduce((prev, curr) => {
      if (!prev) return '';

      if (curr.includes('[') && curr.includes(']')) {
        const arrayName = curr.split('[')[0];
        const index = parseInt(curr.split('[')[1].split(']')[0]);
        const prop = curr.split('.')[1];

        if (!prev[arrayName] || !prev[arrayName][index]) return '';
        return prop ? prev[arrayName][index][prop] : prev[arrayName][index];
      }

      return prev[curr];
    }, obj);
  };

  const setNestedValue = (obj, path, value) => {
    const result = { ...obj };

    if (path.includes('[') && path.includes(']')) {
      const [arrayPath, restPath] = path.split('.');
      const arrayName = arrayPath.split('[')[0];
      const index = parseInt(arrayPath.split('[')[1].split(']')[0]);

      if (restPath) {
        result[arrayName] = [...obj[arrayName]];
        result[arrayName][index] = {
          ...result[arrayName][index],
          [restPath]: value
        };
      } else {
        result[arrayName] = [...obj[arrayName]];
        result[arrayName][index] = value;
      }
      return result;
    }

    if (path.includes('.')) {
      const [parent, child] = path.split('.');
      result[parent] = { ...obj[parent], [child]: value };
      return result;
    }

    return { ...obj, [path]: value };
  };

  const handleFormChange = async (field, value) => {
    if (!isEditingAllowed) {
      return;
    }

    // Convert string values to numbers for number fields
    const numberFields = ['height', 'weight', 'age', 'fat_mass', 'lean_mass', 'total_mass', 'body_fat_percentage', 'muscle_mass', 'bone_density'];

    if (numberFields.includes(field)) {
      // Convert to number or null if empty
      const numberValue = value === '' ? null : Number(value);
      setFormData(prevData => setNestedValue(prevData, field, numberValue));
      return;
    }

    // 1. Image upload logic
    if (field.endsWith('.url') && typeof value === 'object' && value instanceof File) {
      try {
        const s3Url = await uploadImageToS3(value);
        setFormData(prevData => setNestedValue(prevData, field, s3Url));
        return;
      } catch (err) {
        toast.error('Failed to upload image');
        return;
      }
    }

    // 2. Body fat percentage calculation
    if (field === "fat_mass" || field === "total_mass") {
      setFormData(prevData => {
        let updatedData = setNestedValue(prevData, field, value);

        const fatMass = parseFloat(field === "fat_mass" ? value : updatedData.fat_mass);
        const totalMass = parseFloat(field === "total_mass" ? value : updatedData.total_mass);
        let bodyFatPercentage = "";
        if (totalMass > 0 && !isNaN(fatMass) && !isNaN(totalMass)) {
          bodyFatPercentage = ((fatMass / totalMass) * 100).toFixed(2);
        }
        updatedData = { ...updatedData, body_fat_percentage: bodyFatPercentage };
        return updatedData;
      });
      return;
    }

    // 3. Generic fallback
    setFormData(prevData => setNestedValue(prevData, field, value));
  };

  const handleBmdChange = (section, idx, field, value) => {
    setFormData(prev =>
    ({
      ...prev,
      bmdSections: {
        ...prev.bmdSections,
        [section]: prev.bmdSections[section].map((row, i) =>
          i === idx ? { ...row, [field]: value } : row
        ),
      },
    })
    );
  };

  const handleBmdImageChange = async (sectionKey, imgOrFile) => {
    // If a File, upload to S3
    if (imgOrFile instanceof File) {
      try {
        const s3Url = await uploadImageToS3(imgOrFile);
        setFormData(prev => ({
          ...prev,
          bmdImages: { ...prev.bmdImages, [sectionKey]: s3Url }
        }));
      } catch (err) {
        toast.error('Failed to upload image');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        bmdImages: { ...prev.bmdImages, [sectionKey]: imgOrFile }
      }));
    }
  };

  const handleSubmit = async (status = 'submitted') => {
    // Validate report unless bypass is enabled
    if (!bypassValidation) {
      const validation = validateDEXAReport(formData);
      if (!validation.isValid) {
        setValidationModal({
          isOpen: true,
          violations: validation.violations,
          action: status
        });
        return;
      }
    }

    if (status === 'published') {
      setIsPublishing(true);
    }

    try {
      const reportData = {
        ...formData,
        created_by: currentUser,
        updated_by: currentUser,
        report_status: status,
      };

      if (status === 'published') {
        // First generate the preview HTML
        const html = await generateReportHTML(reportData);

        console.log('Generated HTML:', html);

        // Create a temporary div to load the HTML and wait for images
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        document.body.appendChild(tempDiv);

        // Wait for all images to load
        const imageLoadPromises = Array.from(tempDiv.getElementsByTagName('img'))
          .map(img => {
            if (img.complete) return Promise.resolve();
            return new Promise(resolve => {
              img.onload = resolve;
              img.onerror = resolve; // Handle failed image loads gracefully
            });
          });

        try {
          // Wait for all images to load with a timeout
          await Promise.race([
            Promise.all(imageLoadPromises),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Image loading timeout')), 10000)) // 10 second timeout
          ]);

          // Generate PDF via backend service
          const pdfBlob = await requestPDF(html);

          // Clean up the temporary div
          document.body.removeChild(tempDiv);

          // Create a File object from the Blob with a proper name
          const patientName = patient?.profile?.name || formData.name || 'patient';
          const orderId = patient?.sukra_order_id || patient?.id || 'unknown';
          const fileName = generatePDFFilename(patientName, 'dexa', orderId);
          
          console.log('🔍 PDF Generation Debug Info:');
          console.log('patient?.profile?.name:', patient?.profile?.name);
          console.log('formData.name:', formData.name);
          console.log('Final patientName used:', patientName);
          console.log('patient?.sukra_order_id:', patient?.sukra_order_id);
          console.log('patient?.id:', patient?.id);
          console.log('Final orderId used:', orderId);
          console.log('📄 Final PDF Filename:', fileName);
          
          const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

          // Upload PDF to S3 using the uploadImageToS3 function
          const pdfUrl = await uploadImageToS3(pdfFile);

          // Create report with PDF URL
          await reportService.createReport(patientId, {
            ...reportData,
            pdf_url: pdfUrl
          });

          toast.success('Report published and PDF generated successfully');
        } catch (error) {
          // Clean up the temporary div in case of error
          if (document.body.contains(tempDiv)) {
            document.body.removeChild(tempDiv);
          }
          throw new Error('Failed to generate PDF: ' + error.message);
        }
      } else {
        // Just save the report data
        await reportService.createReport(patientId, reportData);
        toast.success('Report saved successfully');
      }

      navigate('/patients');
    } catch (error) {
      toast.error('Failed to save report');
      console.error('Error saving report:', error);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCancel = () => {
    navigate('/patients');
  };

  const handleForceCreate = () => {
    setBypassValidation(true);
    setValidationModal({ isOpen: false, violations: [], action: null });

    // Execute the pending action
    if (validationModal.action === 'preview') {
      handlePreview();
    } else if (validationModal.action === 'submitted' || validationModal.action === 'published') {
      handleSubmit(validationModal.action);
    }
  };

  const handleSectionChange = (index) => {
    setCurrentSection(index);
  };

  const goToNextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const goToPreviousSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handlePreview = async () => {
    // Validate report unless bypass is enabled
    if (!bypassValidation) {
      const validation = validateDEXAReport(formData);
      if (!validation.isValid) {
        setValidationModal({
          isOpen: true,
          violations: validation.violations,
          action: 'preview'
        });
        return;
      }
    }

    const reportData = {
      patientName: patient?.profile?.name,
      height: patient?.profile?.height,
      weight: patient?.profile?.weight,
      age: patient?.profile?.age,
      gender: patient?.profile?.gender,
      dob: patient?.profile?.dob,
      patientId: patient?.profile?.id,
      reportId: patient.id,
      ...formData
    };

    // Open preview in a dedicated route
    navigate(`/report/${patientId}/preview`, { state: { reportData } });
  };

  const handleAsymmetryChange = (part, field, value) => {
    setFormData(prev => ({
      ...prev,
      asymmetry: {
        ...prev.asymmetry,
        ...(field === null
          ? { [part]: value }
          : {
            limbComposition: {
              ...prev.asymmetry.limbComposition,
              [part]: {
                ...prev.asymmetry.limbComposition[part],
                [field]: value
              }
            }
          }
        )
      }
    }));
  };

  const handleAsymmetryImageChange = async (value) => {
    if (value instanceof File) {
      try {
        const s3Url = await uploadImageToS3(value);
        setFormData(prev => ({
          ...prev,
          asymmetry: {
            ...prev.asymmetry,
            imageUrl: s3Url
          }
        }));
      } catch (err) {
        toast.error('Failed to upload image');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        asymmetry: {
          ...prev.asymmetry,
          imageUrl: value
        }
      }));
    }
  };

  const renderField = (field) => {
    if (field.component === 'bodyAsymmetry') {
      return (
        <BodyAsymmetrySection
          ffmi={formData.asymmetry?.ffmi}
          almi={formData.asymmetry?.almi}
          imageUrl={formData.asymmetry?.imageUrl}
          limbComposition={formData.asymmetry?.limbComposition || {
            rightArm: { fat: '', lean: '', bmc: '' },
            leftArm: { fat: '', lean: '', bmc: '' },
            rightLeg: { fat: '', lean: '', bmc: '' },
            leftLeg: { fat: '', lean: '', bmc: '' }
          }}
          onChange={handleAsymmetryChange}
          onImageChange={handleAsymmetryImageChange}
          disabled={!isEditingAllowed}
        />
      );
    }

    if (field.component === 'bmdTable') {
      return (
        <BMDTableSection
          bmdSections={formData.bmdSections}
          bmdImages={formData.bmdImages}
          onBmdChange={handleBmdChange}
          onBmdImageChange={handleBmdImageChange}
          isEditingAllowed={isEditingAllowed}
        />
      );
    }

    const fieldValue = getNestedValue(formData, field.name);

    switch (field.type) {
      case 'input':
        return (
          <Input
            key={field.name}
            label={field.label}
            id={field.name}
            name={field.name}
            type={field.inputType || 'text'}
            value={fieldValue || ''}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            className="mb-4"
            disabled={!isEditingAllowed}
          />
        );

      case 'textarea':
        return (
          <TextInputSection
            key={field.name}
            label={field.label}
            value={fieldValue || ''}
            onChange={(value) => handleFormChange(field.name, value)}
            disabled={!isEditingAllowed}
          />
        );

      case 'image':
        return (
          <ImageUploadSection
            key={field.name}
            label={field.label}
            value={fieldValue || ''}
            onChange={async (val) => {
              // If val is a File, upload to S3 first.
              if (val && typeof val === 'object' && val instanceof File) {
                await handleFormChange(field.name, val);
              } else {
                handleFormChange(field.name, val);
              }
            }}
            disabled={!isEditingAllowed}
            uploadImageToS3={uploadImageToS3}
          />
        );

      case 'select':
        return (
          <Select
            key={field.name}
            label={field.label}
            id={field.name}
            name={field.name}
            value={fieldValue || ''}
            options={field.options || []}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            className="mb-4"
            disabled={!isEditingAllowed}
          />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {isPublishing && <PublishingOverlay />}
      <ValidationModal
        isOpen={validationModal.isOpen}
        violations={validationModal.violations}
        onClose={() => setValidationModal({ isOpen: false, violations: [], action: null })}
        onForceCreate={handleForceCreate}
        title="DEXA Report Validation Failed"
      />
      <div className="flex flex-col md:flex-row gap-6">
        <div className="w-full md:w-64">
          <FormSidebar
            sections={sections}
            currentSection={currentSection}
            onSectionChange={handleSectionChange}
            patient={patient}
            existingReport={existingReport}
            isEditingAllowed={isEditingAllowed}
            currentDateTime={currentDateTime}
          />
        </div>

        <div className="flex-1">
          <Card>

            {isEditingAllowed && currentSection === 0 && (
              <DicomFileUpload 
                onUploadSuccess={(dicomData) => {
                  // Update form data with DICOM file data
                  setFormData(prev => ({
                    ...prev,
                    // Basic patient demographics
                    name: dicomData.patientName || prev.name,
                    height: dicomData.patientSize || prev.height,
                    weight: dicomData.patientWeight || prev.weight,
                    age: dicomData.patientAge || prev.age,
                    gender: dicomData.patientSex || prev.gender,
                    patient_id: dicomData.patientId || prev.patient_id,
                    dob: dicomData.patientDob || prev.dob,
                    
                    // Exam and body composition data
                    date_of_exam: dicomData.dateOfExam || prev.date_of_exam,
                    fat_mass: dicomData.fatMass || prev.fat_mass,
                    lean_mass: dicomData.leanMass || prev.lean_mass,
                    total_mass: dicomData.totalMass || prev.total_mass,
                    body_fat_percentage: dicomData.bodyFatPercentage || prev.body_fat_percentage,
                    bmc: dicomData.bmc || prev.bmc,
                    
                    // Additional body composition metrics
                    visceral_fat_area: dicomData.visceralFatArea || prev.visceral_fat_area,
                    visceral_fat: dicomData.visceralFatMass || prev.visceral_fat,
                    andriod_gynoid_fat: dicomData.androidGynoidRatio || prev.andriod_gynoid_fat,
                    gynoid_fat: dicomData.gynoidFat || prev.gynoid_fat,
                    android_fat: dicomData.androidFat || prev.android_fat,
                    subcutaneous_fat_mass: dicomData.subcutaneousFatMass || prev.subcutaneous_fat_mass,
                    subcutaneous_fat_area: dicomData.subcutaneousFatArea || prev.subcutaneous_fat_area,
                    
                    // Institution and device information
                    institution_name: dicomData.institutionName || prev.institution_name,
                    institution_address: dicomData.institutionAddress || prev.institution_address,
                    department_name: dicomData.institutionalDepartmentName || prev.department_name,
                    device_manufacturer: dicomData.manufacturer || prev.device_manufacturer,
                    device_model: dicomData.deviceModel || prev.device_model,
                    device_serial: dicomData.deviceSerialNumber || prev.device_serial,
                    
                    // BMD data with images and values - use the new structure directly
                    bmdSections: dicomData.bmdSections ? dicomData.bmdSections : prev.bmdSections,
                    
                    // BMD images - use the new structure directly
                    bmdImages: dicomData.bmdImages || prev.bmdImages,
                    
                    // Body asymmetry data
                    asymmetry: dicomData.bodyAsymmetryData
                  }));
                  
                  console.log('DICOM data mapped to form:', dicomData);
                  toast.success('Form filled with DICOM data successfully');
                }}
                disabled={!isEditingAllowed}
              />
            )}
            
            <div className="mb-4">
              <h2 className="text-xl font-semibold">
                {sections[currentSection].label}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {isEditingAllowed
                  ? "Complete all fields in this section before proceeding"
                  : "View the information in this section"}
              </p>
            </div>

            <FormSection>
              <div className="space-y-6">
                {sections[currentSection].component ? (
                  renderField({ component: sections[currentSection].component })
                ) : (
                  sections[currentSection].fields.map(field => renderField(field))
                )}
              </div>
            </FormSection>

            <div className="flex justify-between mt-6">
              <Button
                variant="secondary"
                onClick={currentSection > 0 ? goToPreviousSection : handleCancel}
              >
                {currentSection > 0 ? 'Previous' : 'Back to List'}
              </Button>

              {currentSection === sections.length - 1 ? (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handlePreview}
                    disabled={submitting}
                  >
                    Preview Report
                  </Button>
                  <Button
                    onClick={() => handleSubmit('submitted')}
                    disabled={submitting}
                    variant="secondary"
                  >
                    {submitting ? 'Saving...' : 'Submit Report'}
                  </Button>
                  <Button
                    onClick={() => handleSubmit('published')}
                    disabled={submitting}
                    className="bg-primary-500 hover:bg-primary-600"
                  >
                    {submitting ? 'Publishing...' : 'Submit & Publish'}
                  </Button>
                </div>
              ) : (
                <Button onClick={goToNextSection}>Next</Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ReportForm;
